/**
 * PagosAuditoriaSection (rc21+) — Sub-tab "Auditoría sync" en Ajustes/Pagos.
 *
 * Permite al admin:
 *   1. **Analizar** (dry-run): GET /sync-audit → cuántas facturas/pagos
 *      están desincronizados con Toku. NO toca Toku.
 *   2. **Sincronizar** (ejecución): POST /sync-pendientes → ensure_invoice
 *      e notify_external_payment idempotentes con rate limit 1s.
 *
 * SEGURIDAD:
 *  - El backend usa flujos idempotentes existentes (ensure_invoice busca
 *    mapping antes de POST; Toku rechaza duplicados; notify_external_payment
 *    chequea local_pago_synced_at).
 *  - Confirmación con modal antes de ejecutar.
 *  - Resultados detallados por item (success/skipped/error).
 *  - Errores caen a Excepciones (rc18 DLQ) automáticamente.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  RefreshCcw, Loader2, AlertCircle, CheckCircle2, XCircle, FileText,
  Wallet, PlayCircle, Search, Ghost, Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiCall, type ApiError } from "@/lib/api";

/* ─── Tipos ─────────────────────────────────────────────────── */
type FacturaPendiente = {
  factura_id: string;
  folio: string;
  fecha_emision: string;
  fecha_vencimiento: string;
  estatus: string;
  total: number;
  saldo: number;
  cliente_codigo: string;
  cliente_razon_social: string;
  empresa_id: number;
  empresa_nombre: string;
};

type FacturaHuerfana = FacturaPendiente & {
  toku_invoice_id_stale: string;
};

type PagoPendiente = {
  pago_id: string;
  pago_folio: string;
  fecha: string;
  monto: number;
  metodo: string;
  factura_folio: string;
  cliente_codigo: string;
  cliente_razon_social: string;
  empresa_nombre: string;
};

type AuditResponse = {
  environment: string;
  facturas_sin_sync_count: number;
  pagos_sin_notificar_count: number;
  facturas_huerfanas_count: number;
  huerfanas_checked: number;
  huerfanas_errors: number;
  huerfanas_truncated: boolean;
  total_pendientes: number;
  facturas_sin_sync: FacturaPendiente[];
  pagos_sin_notificar: PagoPendiente[];
  facturas_huerfanas: FacturaHuerfana[];
};

type SyncItem = {
  factura_id?: string;
  pago_id?: string;
  folio?: string;
  factura_folio?: string;
  result: "success" | "skipped" | "error";
  error?: string;
  skipped_reason?: string;
  toku_invoice_id?: string;
  old_toku_invoice_id?: string;
  toku_status?: string;
};

type SyncRunResponse = {
  facturas: {
    processed: number;
    success: number;
    skipped: number;
    error: number;
    items: SyncItem[];
    message?: string;
  };
  pagos: {
    processed: number;
    success: number;
    skipped: number;
    error: number;
    items: SyncItem[];
    message?: string;
  };
  huerfanas: {
    processed: number;
    success: number;
    skipped: number;
    error: number;
    items: SyncItem[];
    message?: string;
  };
};

/* ─── Hooks ─────────────────────────────────────────────────── */
function useSyncAudit(enabled: boolean, checkHuerfanas: boolean) {
  return useQuery<AuditResponse>({
    queryKey: ["toku-sync-audit", { checkHuerfanas }],
    queryFn: () => apiCall<AuditResponse>(
      "get",
      `/api/v1/integrations/payments/sync-audit${checkHuerfanas ? "?check_huerfanas=true" : ""}`,
      // rc14: la verificación de huérfanas hace 1 GET/seg a Toku — puede tardar
      // hasta ~200 segundos (cap max_check=200). Subimos timeout a 6 min.
      { timeout: checkHuerfanas ? 360_000 : 30_000 },
    ),
    enabled,
    staleTime: 0,
  });
}

/* ─── Componente principal ──────────────────────────────────── */
export function PagosAuditoriaSection() {
  const qc = useQueryClient();
  const [hasAudited, setHasAudited] = useState(false);
  const [checkHuerfanas, setCheckHuerfanas] = useState(false);
  const { data: audit, isLoading, isFetching, refetch } = useSyncAudit(hasAudited, checkHuerfanas);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastRun, setLastRun] = useState<SyncRunResponse | null>(null);

  const runMut = useMutation({
    mutationFn: (body: {
      all_facturas: boolean; all_pagos: boolean; all_huerfanas: boolean; max_items: number
    }) =>
      apiCall<SyncRunResponse>("post", "/api/v1/integrations/payments/sync-pendientes", {
        json: body,
        // rc14: el sync masivo puede tardar 1 seg/item × hasta 100 items × 3 categorías.
        // Subimos timeout a 10 min para no cortar antes que termine el backend.
        timeout: 600_000,
      }),
    onSuccess: (data) => {
      setLastRun(data);
      const total = data.facturas.processed + data.pagos.processed + data.huerfanas.processed;
      const errors = data.facturas.error + data.pagos.error + data.huerfanas.error;
      if (errors === 0) {
        toast.success(`Sincronización completa: ${total} items procesados`);
      } else {
        toast.warning(`Procesados: ${total}, errores: ${errors}. Revisa Excepciones.`);
      }
      qc.invalidateQueries({ queryKey: ["toku-sync-audit"] });
      qc.invalidateQueries({ queryKey: ["toku-exceptions-counts"] });
      setConfirmOpen(false);
    },
    onError: (e: ApiError) => {
      toast.error(e.detail || "Error al ejecutar sincronización");
      setConfirmOpen(false);
    },
  });

  const total = audit ? audit.total_pendientes : 0;
  const noPendientes = hasAudited && !isFetching && total === 0;

  // Estimación tiempo: 1s por item para facturas + pagos, y 1s por huérfana
  const tiempoEstimadoSeg = audit
    ? audit.facturas_sin_sync_count + audit.pagos_sin_notificar_count + audit.facturas_huerfanas_count
    : 0;

  return (
    <div className="flex flex-col gap-4">
      {/* ─── Intro card ─── */}
      <div className="rounded border border-primary/30 bg-primary/5 p-3 text-xs">
        <p className="text-muted-foreground">
          <strong className="text-foreground">Auditoría de sincronización Toku.</strong>{" "}
          Detecta tres clases de problemas: facturas locales sin mapping a Toku,
          pagos aplicados que no se notificaron a Toku, y{" "}
          <strong className="text-foreground">facturas huérfanas</strong> (ByteISP tiene
          mapping pero la invoice ya no existe del lado de Toku — purgada por ETL externo,
          cleanup, etc.).
        </p>
        <p className="mt-2 text-muted-foreground">
          El análisis básico es <strong className="text-foreground">solo lectura</strong>.
          La detección de huérfanas hace verificación en vivo contra Toku (1 GET/seg, hasta 200
          facturas por corrida). La sincronización es{" "}
          <strong className="text-foreground">idempotente</strong>: si una factura ya existe en
          Toku se omite automáticamente — no hay riesgo de duplicar.
        </p>
      </div>

      {/* ─── Botón analizar ─── */}
      {!hasAudited && (
        <div className="rounded border bg-card p-6 text-center">
          <Search size={28} className="mx-auto mb-2 text-muted-foreground" />
          <p className="mb-3 text-sm font-medium">Analizar desincronización</p>
          <p className="mb-4 text-xs text-muted-foreground">
            Cuenta cuántas facturas y pagos están pendientes de sincronizar con Toku.
            Detección de huérfanas la activas con un botón aparte (es más lento).
          </p>
          <Button onClick={() => { setHasAudited(true); refetch(); }}>
            <Search size={14} className="mr-1" />
            Iniciar análisis
          </Button>
        </div>
      )}

      {/* ─── Resultado del análisis ─── */}
      {hasAudited && isLoading && (
        <div className="rounded border bg-card p-6 text-center">
          <Loader2 size={28} className="mx-auto mb-2 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Analizando…</p>
        </div>
      )}

      {hasAudited && audit && !isLoading && (
        <>
          {/* Resumen — 4 cards */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <SummaryCard
              icon={FileText}
              label="Sin sincronizar"
              count={audit.facturas_sin_sync_count}
              color={audit.facturas_sin_sync_count === 0 ? "green" : "amber"}
              hint="Facturas sin mapping local a Toku"
            />
            <SummaryCard
              icon={Wallet}
              label="Pagos sin notificar"
              count={audit.pagos_sin_notificar_count}
              color={audit.pagos_sin_notificar_count === 0 ? "green" : "amber"}
              hint="Pagos aplicados que no llegaron a Toku"
            />
            <SummaryCard
              icon={Ghost}
              label="Huérfanas en Toku"
              count={audit.facturas_huerfanas_count}
              color={
                !checkHuerfanas
                  ? "muted"
                  : audit.facturas_huerfanas_count === 0
                    ? "green"
                    : "red"
              }
              hint={
                checkHuerfanas
                  ? `${audit.huerfanas_checked} verificadas vs Toku`
                  : "Activar verificación para detectar"
              }
            />
            <SummaryCard
              icon={CheckCircle2}
              label="Total pendientes"
              count={audit.total_pendientes}
              color={audit.total_pendientes === 0 ? "green" : "amber"}
              hint="Suma de las 3 categorías"
            />
          </div>

          {/* Toggle huérfanas (off por default — es lento) */}
          {!checkHuerfanas && (
            <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
              <div className="flex items-start justify-between gap-3">
                <div className="flex gap-2">
                  <Ghost size={14} className="mt-0.5 shrink-0 text-amber-500" />
                  <div className="text-muted-foreground">
                    <strong className="text-foreground">
                      Detección de huérfanas no ejecutada.
                    </strong>{" "}
                    Hay facturas con mapping local que pueden no existir realmente
                    en Toku (purga ETL, intervención manual, etc.). Verificar requiere
                    1 GET por factura (1 seg c/u, hasta 200 facturas).
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setCheckHuerfanas(true); }}
                  disabled={isFetching}
                  className="shrink-0"
                >
                  {isFetching ? (
                    <Loader2 size={12} className="mr-1 animate-spin" />
                  ) : (
                    <Zap size={12} className="mr-1" />
                  )}
                  Verificar huérfanas
                </Button>
              </div>
            </div>
          )}

          {checkHuerfanas && audit.huerfanas_truncated && (
            <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-muted-foreground">
              <strong className="text-foreground">Verificación truncada.</strong>{" "}
              Se revisaron las primeras 200 facturas con mapping. Vuelve a correr
              después de re-sincronizar para auditar las siguientes.
            </div>
          )}

          {checkHuerfanas && audit.huerfanas_errors > 0 && (
            <div className="rounded border border-orange-500/30 bg-orange-500/5 p-3 text-xs text-muted-foreground">
              <strong className="text-foreground">
                {audit.huerfanas_errors} factura(s) no pudieron verificarse
              </strong>{" "}
              (timeout, error de red o respuesta 5xx). NO se marcaron como
              huérfanas — re-intenta más tarde.
            </div>
          )}

          {/* Acción principal */}
          {noPendientes ? (
            <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs">
              <div className="flex gap-2">
                <CheckCircle2 size={14} className="shrink-0 text-emerald-500" />
                <span className="text-muted-foreground">
                  <strong className="text-foreground">Todo sincronizado.</strong>{" "}
                  No hay items pendientes para el ambiente <em>{audit.environment}</em>
                  {checkHuerfanas ? " (huérfanas incluidas)" : " (huérfanas no verificadas)"}.
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 rounded border bg-card p-3">
              <p className="text-xs text-muted-foreground">
                {audit.total_pendientes} item(s) pendientes en ambiente{" "}
                <strong className="text-foreground">{audit.environment}</strong>.
                Tardará ~{tiempoEstimadoSeg}s (1s entre llamadas).
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => refetch()} disabled={isFetching}
                >
                  {isFetching ? (
                    <Loader2 size={12} className="mr-1 animate-spin" />
                  ) : (
                    <RefreshCcw size={12} className="mr-1" />
                  )}
                  Re-analizar
                </Button>
                <Button size="sm" onClick={() => setConfirmOpen(true)}>
                  <PlayCircle size={12} className="mr-1" />
                  Sincronizar todos
                </Button>
              </div>
            </div>
          )}

          {/* Listas */}
          {audit.facturas_sin_sync.length > 0 && (
            <FacturasList items={audit.facturas_sin_sync} />
          )}
          {audit.facturas_huerfanas.length > 0 && (
            <HuerfanasList items={audit.facturas_huerfanas} />
          )}
          {audit.pagos_sin_notificar.length > 0 && (
            <PagosList items={audit.pagos_sin_notificar} />
          )}
        </>
      )}

      {/* ─── Resultado de la última ejecución ─── */}
      {lastRun && <RunResultPanel result={lastRun} />}

      {/* ─── Modal confirmación ─── */}
      {confirmOpen && audit && (
        <Dialog open onOpenChange={(o) => { if (!o) setConfirmOpen(false); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar sincronización masiva</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-2 py-2 text-sm">
              <p>
                Se va a sincronizar contra Toku ambiente{" "}
                <strong>{audit.environment}</strong>:
              </p>
              <ul className="ml-4 list-disc text-xs text-muted-foreground">
                {audit.facturas_sin_sync_count > 0 && (
                  <li>
                    <strong>{audit.facturas_sin_sync_count}</strong> factura(s) sin
                    mapping local → crear en Toku
                  </li>
                )}
                {audit.facturas_huerfanas_count > 0 && (
                  <li>
                    <strong>{audit.facturas_huerfanas_count}</strong> huérfana(s) →
                    <em> recrear</em> en Toku (mapping local apuntaba a invoice borrada)
                  </li>
                )}
                {audit.pagos_sin_notificar_count > 0 && (
                  <li>
                    <strong>{audit.pagos_sin_notificar_count}</strong> pago(s) sin
                    notificar → notificar a Toku
                  </li>
                )}
              </ul>
              <p className="text-xs text-muted-foreground">
                Idempotente — items ya sincronizados se omiten. Cualquier fallo aparece
                en la pestaña <em>Excepciones</em>. Tardará ~{tiempoEstimadoSeg} segundos.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmOpen(false)}
                disabled={runMut.isPending}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={() =>
                  runMut.mutate({
                    all_facturas: audit.facturas_sin_sync_count > 0,
                    all_pagos: audit.pagos_sin_notificar_count > 0,
                    all_huerfanas: audit.facturas_huerfanas_count > 0,
                    max_items: 100,
                  })
                }
                disabled={runMut.isPending}
              >
                {runMut.isPending ? (
                  <>
                    <Loader2 size={12} className="mr-1 animate-spin" />
                    Sincronizando…
                  </>
                ) : (
                  <>
                    <PlayCircle size={12} className="mr-1" />
                    Confirmar y ejecutar
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/* ─── Sub-componentes ─────────────────────────────────────── */
function SummaryCard({
  icon: Icon, label, count, color, hint,
}: {
  icon: typeof FileText;
  label: string;
  count: number;
  color: "amber" | "green" | "red" | "muted";
  hint?: string;
}) {
  const colorCls = {
    amber: "text-amber-500",
    green: "text-emerald-500",
    red: "text-red-500",
    muted: "text-muted-foreground",
  }[color];
  const display = color === "muted" ? "—" : String(count);
  return (
    <div className="rounded border bg-card p-3">
      <div className="flex items-center gap-2">
        <Icon size={16} className={colorCls} />
        <div className="min-w-0 flex-1">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className={`text-xl font-bold ${colorCls}`}>{display}</div>
          {hint && (
            <div className="mt-0.5 text-[10px] text-muted-foreground/80">{hint}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function FacturasList({ items }: { items: FacturaPendiente[] }) {
  return (
    <div className="rounded border bg-card">
      <div className="border-b px-3 py-2 text-xs font-semibold">
        Facturas sin sincronizar ({items.length})
      </div>
      <div className="max-h-80 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-muted/50">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Folio</th>
              <th className="px-2 py-1.5 text-left font-medium">Cliente</th>
              <th className="px-2 py-1.5 text-left font-medium">Empresa</th>
              <th className="px-2 py-1.5 text-right font-medium">Saldo</th>
              <th className="px-2 py-1.5 text-left font-medium">Estado</th>
            </tr>
          </thead>
          <tbody>
            {items.map((f) => (
              <tr key={f.factura_id} className="border-t">
                <td className="px-2 py-1.5 font-mono">{f.folio}</td>
                <td className="px-2 py-1.5">
                  <div className="font-medium">{f.cliente_razon_social}</div>
                  <div className="text-[10px] text-muted-foreground">{f.cliente_codigo}</div>
                </td>
                <td className="px-2 py-1.5 text-muted-foreground">{f.empresa_nombre}</td>
                <td className="px-2 py-1.5 text-right font-mono">${f.saldo.toFixed(2)}</td>
                <td className="px-2 py-1.5 text-muted-foreground">{f.estatus}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HuerfanasList({ items }: { items: FacturaHuerfana[] }) {
  return (
    <div className="rounded border border-red-500/30 bg-card">
      <div className="border-b border-red-500/20 bg-red-500/5 px-3 py-2 text-xs font-semibold">
        <Ghost size={12} className="mr-1 inline" />
        Huérfanas en Toku ({items.length})
        <span className="ml-2 font-normal text-muted-foreground">
          — mapping local apunta a invoice que NO existe en Toku. Al sincronizar se{" "}
          <em>recrean</em>.
        </span>
      </div>
      <div className="max-h-80 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-muted/50">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Folio</th>
              <th className="px-2 py-1.5 text-left font-medium">Cliente</th>
              <th className="px-2 py-1.5 text-left font-medium">Empresa</th>
              <th className="px-2 py-1.5 text-right font-medium">Saldo</th>
              <th className="px-2 py-1.5 text-left font-medium">Toku ID (stale)</th>
            </tr>
          </thead>
          <tbody>
            {items.map((f) => (
              <tr key={f.factura_id} className="border-t">
                <td className="px-2 py-1.5 font-mono">{f.folio}</td>
                <td className="px-2 py-1.5">
                  <div className="font-medium">{f.cliente_razon_social}</div>
                  <div className="text-[10px] text-muted-foreground">{f.cliente_codigo}</div>
                </td>
                <td className="px-2 py-1.5 text-muted-foreground">{f.empresa_nombre}</td>
                <td className="px-2 py-1.5 text-right font-mono">${f.saldo.toFixed(2)}</td>
                <td className="px-2 py-1.5 font-mono text-[10px] text-muted-foreground">
                  {f.toku_invoice_id_stale.slice(0, 18)}…
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PagosList({ items }: { items: PagoPendiente[] }) {
  return (
    <div className="rounded border bg-card">
      <div className="border-b px-3 py-2 text-xs font-semibold">
        Pagos sin notificar a Toku ({items.length})
      </div>
      <div className="max-h-80 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-muted/50">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Folio pago</th>
              <th className="px-2 py-1.5 text-left font-medium">Factura</th>
              <th className="px-2 py-1.5 text-left font-medium">Cliente</th>
              <th className="px-2 py-1.5 text-right font-medium">Monto</th>
              <th className="px-2 py-1.5 text-left font-medium">Método</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.pago_id} className="border-t">
                <td className="px-2 py-1.5 font-mono">{p.pago_folio}</td>
                <td className="px-2 py-1.5 font-mono">{p.factura_folio}</td>
                <td className="px-2 py-1.5">
                  <div className="font-medium">{p.cliente_razon_social}</div>
                  <div className="text-[10px] text-muted-foreground">{p.cliente_codigo}</div>
                </td>
                <td className="px-2 py-1.5 text-right font-mono">${p.monto.toFixed(2)}</td>
                <td className="px-2 py-1.5 text-muted-foreground">{p.metodo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RunResultPanel({ result }: { result: SyncRunResponse }) {
  const totalProc =
    result.facturas.processed + result.pagos.processed + result.huerfanas.processed;
  const totalSuc =
    result.facturas.success + result.pagos.success + result.huerfanas.success;
  const totalSkip =
    result.facturas.skipped + result.pagos.skipped + result.huerfanas.skipped;
  const totalErr =
    result.facturas.error + result.pagos.error + result.huerfanas.error;

  const allItems: Array<SyncItem & { kind: string }> = [
    ...result.facturas.items.map((it) => ({ ...it, kind: "factura" })),
    ...result.huerfanas.items.map((it) => ({ ...it, kind: "huérfana" })),
    ...result.pagos.items.map((it) => ({ ...it, kind: "pago" })),
  ];

  return (
    <div className="rounded border bg-card">
      <div className="border-b px-3 py-2 text-xs font-semibold">
        Última ejecución — {totalProc} procesados
      </div>
      <div className="grid grid-cols-4 gap-2 p-3 text-xs">
        <Stat icon={CheckCircle2} label="Sincronizados" value={totalSuc} color="text-emerald-500" />
        <Stat icon={AlertCircle} label="Omitidos" value={totalSkip} color="text-muted-foreground" />
        <Stat icon={XCircle} label="Errores" value={totalErr} color="text-red-500" />
        <Stat icon={RefreshCcw} label="Total" value={totalProc} color="text-foreground" />
      </div>
      {allItems.length > 0 && (
        <div className="max-h-60 overflow-y-auto border-t">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted/50">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">Tipo</th>
                <th className="px-2 py-1.5 text-left font-medium">Item</th>
                <th className="px-2 py-1.5 text-left font-medium">Resultado</th>
                <th className="px-2 py-1.5 text-left font-medium">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {allItems.map((it, i) => (
                <tr key={i} className="border-t">
                  <td className="px-2 py-1.5 text-muted-foreground">{it.kind}</td>
                  <td className="px-2 py-1.5 font-mono">{it.folio ?? it.factura_folio}</td>
                  <td className="px-2 py-1.5">
                    <span className={
                      it.result === "success" ? "text-emerald-500" :
                      it.result === "error" ? "text-red-500" :
                      "text-muted-foreground"
                    }>
                      {it.result}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-muted-foreground">
                    {it.error || it.skipped_reason ||
                      (it.old_toku_invoice_id && it.toku_invoice_id
                        ? `${it.old_toku_invoice_id.slice(0,14)}… → ${it.toku_invoice_id.slice(0,14)}…`
                        : it.toku_invoice_id || it.toku_status || "—")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({
  icon: Icon, label, value, color,
}: { icon: typeof CheckCircle2; label: string; value: number; color: string }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={14} className={color} />
      <div>
        <div className="text-[10px] text-muted-foreground">{label}</div>
        <div className={`font-bold ${color}`}>{value}</div>
      </div>
    </div>
  );
}
